import { FormattedText } from "@faire/deprecated-retailer-visitor-shared/components/FormattedText";
import { EcoFriendlyC2PageImageSlider } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Components/EcoFriendlyC2PageImageSlider";
import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { useMilestone } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { Decorative } from "@faire/design-tokens";
import { ResponsibleConsumption } from "@faire/slate/icons/ResponsibleConsumption";
import { ShareHeart } from "@faire/slate/icons/ShareHeart";
import { Sprout } from "@faire/slate/icons/Sprout";
import { trackEcoFriendlyDefinitionBannerShowMoreButtonClick } from "@faire/web-api--source/events/ecoFriendly/click/definitionBannerShowMoreButton";
import { HAS_SEEN_ECO_FRIENDLY_CATEGORY_PAGE } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/HAS_SEEN_ECO_FRIENDLY_CATEGORY_PAGE";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography, TypographyStyles } from "@faire/web/slate/Typography";
import { useState } from "@faire/web/ui/hooks/useState";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import isUndefined from "lodash/isUndefined";
import React, { useEffect, useRef } from "react";
import Slider from "react-slick";
import styled from "styled-components";

const getBannerHeaderMessage = (
  headerText: string,
  descriptionText: string
) => {
  return (
    <>
      <Typography variant="pageHeaderSerifRegular">{headerText}</Typography>
      <StyledTypography>
        <FormattedText text={descriptionText} />
      </StyledTypography>
    </>
  );
};
const getBannerBulletMessageUpdate = (collapsed: boolean) => (
  <AnimatedComponent $collapsed={collapsed}>
    <ResponsiveContainer>
      <IconWrapper>
        <Sprout fontSize="20px" color={Decorative.icon.orange} />
      </IconWrapper>
      <Typography variant="paragraphSansRegular">
        <b>
          <StrictLocalMsg
            id="HY6DE7"
            defaultMessage="Product Materials"
            description={{
              text: "Bullet item for eco-friendly definition banner",
              img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
            }}
          />
        </b>
        <br />
        <StrictLocalMsg
          id="1S7tyq"
          defaultMessage="Products are made with recycled, upcycled, vegan, organic, or plastic-free materials, or are refill products."
          description={{
            text: "Definition of eco-friendly product materials at Faire",
            img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
          }}
        />
      </Typography>
    </ResponsiveContainer>

    <ResponsiveContainer>
      <IconWrapper>
        <ResponsibleConsumption
          fontSize="20px"
          color={Decorative.icon.orange}
        />
      </IconWrapper>
      <Typography variant="paragraphSansRegular">
        <b>
          <StrictLocalMsg
            id="K5Lx/f"
            defaultMessage="Packaging"
            description={{
              text: "Bullet item for eco-friendly definition banner",
              img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
            }}
          />
        </b>
        <br />
        <StrictLocalMsg
          id="x5SFL9"
          defaultMessage="Product packaging is reduced-weight or is made with recycled, recyclable, or plastic-free materials."
          description={{
            text: "Definition of eco-friendly packaging at Faire",
            img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
          }}
        />
      </Typography>
    </ResponsiveContainer>

    <ResponsiveContainer>
      <IconWrapper>
        <ShareHeart fontSize="20px" color={Decorative.icon.orange} />
      </IconWrapper>
      <Typography variant="paragraphSansRegular">
        <b>
          <StrictLocalMsg
            id="dRCT7k"
            defaultMessage="Production"
            description={{
              text: "Bullet item for eco-friendly definition banner",
              img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
            }}
          />
        </b>
        <br />
        <StrictLocalMsg
          id="Z7bHH+"
          defaultMessage="Products are created with ethical sourcing, low-impact transport, renewable energy, fair trade, or cruelty-free methods."
          description={{
            text: "Definition of eco-friendly production at Faire",
            img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
          }}
        />
      </Typography>
    </ResponsiveContainer>

    <ResponsiveBottomContainer>
      <Typography variant="labelSansRegular">
        <StrictLocalMsg
          id="BVETn/"
          defaultMessage="This set of criteria has been developed in partnership with Bluebird (powered by rePurpose Global), a software solution that helps brands decarbonize and reduce waste from their products."
          description={{
            text: "Disclaimer for eco-friendly criteria",
            img: "https://cdn.faire.com/fastly/98e4879f94e4214d03cf1ee59398d366f16f64136a69fe24da8ab1338c5cd94a.png",
          }}
        />
      </Typography>
    </ResponsiveBottomContainer>
  </AnimatedComponent>
);

export const EcoFriendlyC2PageBanner: React.FC = () => {
  const { layoutElement, onCTAClick, requestId } = React.useContext(Context);
  const { isTabletAndAbove, isDesktopAndAbove } = useViewport();
  const [collapsed, setCollapsed] = useState(true);
  const sliderRef = useRef<Slider | null>(null);
  const isSSR = useIsSSR();

  const imageTextTiles = layoutElement.layout_element_data
    .map((i) => i.image_text_tile)
    .filter(isNotUndefined)
    .filter((i) => i.image?.url);

  const shouldShowImageSlider =
    imageTextTiles.length > 0 && (isDesktopAndAbove || !collapsed);
  const {
    hasOccurred: hasSeenEcoFriendlyCategoryPage,
    setHasOccurred: setHasSeenEcoFriendlyCategoryPage,
  } = useMilestone(HAS_SEEN_ECO_FRIENDLY_CATEGORY_PAGE);

  const onClick = () => {
    onCTAClick?.(
      layoutElement.on_click_event_name ?? "EcoFriendlyC2PageBanner",
      requestId
    );
    if (collapsed) {
      trackEcoFriendlyDefinitionBannerShowMoreButtonClick();
    }
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (!hasSeenEcoFriendlyCategoryPage && isTabletAndAbove) {
      setCollapsed(false);
      setHasSeenEcoFriendlyCategoryPage();
    }
  }, [
    hasSeenEcoFriendlyCategoryPage,
    setHasSeenEcoFriendlyCategoryPage,
    isTabletAndAbove,
  ]);

  if (
    isUndefined(layoutElement.main_text) ||
    isUndefined(layoutElement.description_text)
  ) {
    return null;
  }

  return (
    <Wrapper>
      <Content>
        {getBannerHeaderMessage(
          layoutElement.main_text,
          layoutElement.description_text
        )}
        <Spacer height={isTabletAndAbove ? "3x" : "2x"} />
        {getBannerBulletMessageUpdate(collapsed)}
        <StyledButton variant="tertiary" onClick={onClick}>
          {collapsed ? (
            <StrictLocalMsg
              id="Egjo5m"
              defaultMessage="Show more"
              description={{
                text: "Show definition bullets",
                img: "https://cdn.faire.com/fastly/1d7257d38109f7c8b6146c9c7178bd685dbf1f0406039559273792df62db8aac.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              id="bMEYt5"
              defaultMessage="Hide"
              description={{
                text: "Hide definition bullets",
                img: "https://cdn.faire.com/fastly/f0887c628c949a375e2c39477c866df59feacfba33e6be05796bbfa32efaecb1.png",
              }}
            />
          )}
        </StyledButton>
      </Content>
      {shouldShowImageSlider ? (
        <EcoFriendlyC2PageImageSlider
          tiles={imageTextTiles}
          sliderRef={sliderRef}
          isSSR={isSSR}
        />
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column-reverse", desktopAndAbove: "row" },
})`
  margin-bottom: ${getSpacing("3x")};
`;

const Content = styled(Column)`
  background-color: ${Decorative.tint.orange};
  width: 100%;
  padding: ${getSpacing("6x")};

  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("3x", "4x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    margin-bottom: ${getSpacing("3x")};
  }
`;

const StyledButton = styled(Button)`
  align-self: flex-start;
`;

const StyledTypography = styled(Typography).attrs({
  variant: "paragraphSansRegular",
})`
  b {
    ${TypographyStyles({
      variant: "paragraphSansMedium",
    })}
  }
`;

const AnimatedComponent = styled.div<{
  $collapsed: boolean;
}>`
  visibility: ${(props) => (props.$collapsed ? "hidden" : "visible")};
  transition:
    max-height 0.5s ease-out,
    visibility 0.5s ease-out,
    opacity 0.5s ease-out;
  opacity: ${(props) => (props.$collapsed ? "0" : "1")};
  max-height: ${(props) => (props.$collapsed ? "0" : "1000px")};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: ${getSpacing("2x")};
  margin-bottom: ${getSpacing("1x")};
  border-radius: 50%;
  background-color: ${Decorative.tint.neutral};
  padding: 0 10px;
`;

const ResponsiveContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${getSpacing("2x")};

  ${responsiveMediaQueries.mobile} {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
  }
`;

const ResponsiveBottomContainer = styled.div`
  margin-left: ${getSpacing("7x")};
  margin-bottom: ${getSpacing("3x")};
  margin-top: ${getSpacing("3x")};

  ${responsiveMediaQueries.mobile} {
    margin-left: 0;
  }
`;
